import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { Checkbox, ListItemText, MenuItem, Select, Stack } from '@mui/material'
import _ from 'lodash'
import { bshgenAPI } from '~/api'
import { useConfirm } from '~/components'
import { bshSlice } from '~/store'
import { useParagraphContext } from './hooks'

const Skip = () => {
  const { t } = useTranslation()
  const { paragraph, editable, onPropertyChange, saveParagraph } =
    useParagraphContext(Skip.name)
  const shops = useSelector(bshSlice.shops)
  const [savingValue, setSavingValue] = useState(null)
  useEffect(() => {
    if (!savingValue) return
    const intId = setTimeout(async () => {
      await saveParagraph({ shops: savingValue })
      setSavingValue(null)
    }, 5000)
    return () => clearTimeout(intId)
  }, [savingValue, saveParagraph])

  const getSkipsFromShops = useCallback(() => {
    return paragraph.shops
      ? Object.keys(paragraph?.shops)
          .map((key) => (paragraph.shops[key].skip ? key : null))
          .filter(Boolean)
      : []
  }, [paragraph.shops])

  const [skips, setSkips] = useState(getSkipsFromShops)
  useEffect(() => {
    queueMicrotask(() => {
      setSkips(getSkipsFromShops())
    })
  }, [getSkipsFromShops])

  const { show } = useConfirm()
  const onSkipChange = useCallback(
    async (selected) => {
      const difference = skips
        .filter((x) => !selected.includes(x))
        .concat(selected.filter((x) => !skips.includes(x)))

      const checked = selected.length > skips.length
      setSkips(selected)

      const property = difference[0]
      if (!property) return

      const value = _.cloneDeep(paragraph.shops) ?? {}
      if (!value[property]) {
        value[property] = {}
      }
      value[property].skip = checked ? 1 : 0
      if (!checked && Object.keys(value[property]).length === 1) {
        delete value[property]
      }
      let url = `${paragraph.model}/${paragraph.id}/children-with-custom-skip/${property}`
      const { customized } = await bshgenAPI(url)
      if (customized) {
        await show({
          title: t('paragraph.customized-skip-reset.title'),
          body: t('paragraph.customized-skip-reset.body'),
          actions: [
            {
              id: 'yes',
              name: t('ui.yes'),
              variant: 'contained',
            },
            {
              id: 'no',
              name: t('ui.no'),
              variant: 'outlined',
            },
          ],
          onClose: async (action) => {
            if (action === 'yes') {
              onPropertyChange('shops', value)
              setSavingValue(value)
            } else {
              setSkips(getSkipsFromShops())
            }
          },
        })
      } else {
        onPropertyChange('shops', value)
        setSavingValue(value)
      }
    },
    [
      getSkipsFromShops,
      onPropertyChange,
      paragraph.id,
      paragraph.model,
      paragraph.shops,
      show,
      skips,
      t,
    ]
  )

  return (
    <Stack direction="row" spacing={2} alignItems="center">
      {editable ? (
        <Select
          displayEmpty
          multiple
          renderValue={(selected) => {
            if (selected.length === 0) {
              return <em>{t('ui.skip')}</em>
            }
            return shops
              .filter(({ slug }) => selected.includes(slug))
              .map(({ short }) => short)
              .join(', ')
          }}
          size="small"
          value={skips}
          onChange={(event) => onSkipChange(event.target.value)}
        >
          {shops.map(({ name, slug }, index) => (
            <MenuItem key={index} value={slug}>
              <Checkbox
                checked={skips.includes(slug)}
                color="default"
                sx={{ pl: 0, py: 0 }}
              />
              <ListItemText primary={name} />
            </MenuItem>
          ))}
        </Select>
      ) : (
        <Stack direction="row" spacing={2} py={1}>
          <span>{t('ui.skip')}</span>
          {shops.map(
            ({ short, slug }, index) =>
              paragraph.skips?.includes(slug) && (
                <span key={index}>{short}</span>
              )
          )}
        </Stack>
      )}
    </Stack>
  )
}

export default Skip
